import { motion } from 'framer-motion';
import { Clock, Eye, AlertTriangle, TrendingDown } from 'lucide-react';

const LaggingIndicatorDeep = () => {
  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="lagging-indicator-deep">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-red-500">
            The Truth About Account Health
          </p>
          <h2 className="font-display text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-3">
            Why Your Dashboard is <span className="text-red-500">Lying</span> to You
          </h2>
        </motion.div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Point 1 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex gap-6 items-start"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-display text-slate-800 text-xl">Amazon's data is delayed — sometimes by days</h3>
              <p className="text-slate-500 mt-2 leading-relaxed">
                When you check your Account Health dashboard, you're looking at data that can be 24-72 hours old. 
                In that time, dozens of orders have been placed, returns initiated, and customer complaints filed. 
                <span className="font-medium text-slate-700"> The dashboard shows you where you were, not where you are.</span>
              </p>
            </div>
          </motion.div>

          {/* Point 2 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex gap-6 items-start"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <Eye className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-display text-slate-800 text-xl">Amazon sees problems before you do</h3>
              <p className="text-slate-500 mt-2 leading-relaxed">
                Amazon's internal systems flag accounts long before the Account Health Rating changes. 
                They track <span className="font-medium text-slate-700">rate of change</span>, not just absolute numbers. 
                Your ODR might be 0.8% today — well under the 1% threshold — but if it jumped from 0.4% last week, 
                Amazon's systems have already taken notice.
              </p>
            </div>
          </motion.div>

          {/* Point 3 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex gap-6 items-start"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-display text-slate-800 text-xl">Warning signs exist — but they're hidden from you</h3>
              <p className="text-slate-500 mt-2 leading-relaxed">
                There are over 50 signals that predict account enforcement. Things like carrier scan delays, 
                message response patterns, return reason clustering, and listing compliance scores. 
                <span className="font-medium text-slate-700"> None of these appear in your Seller Central dashboard</span> — 
                but they all feed into Amazon's enforcement algorithms.
              </p>
            </div>
          </motion.div>

          {/* Point 4 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex gap-6 items-start"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-display text-slate-800 text-xl">By the time your rating drops, it's already too late</h3>
              <p className="text-slate-500 mt-2 leading-relaxed">
                Account Health Rating is the <span className="font-medium text-slate-700">output</span>, not the input. 
                It's the score Amazon shows you <span className="font-medium text-slate-700">after</span> they've already 
                decided something is wrong. The real decision happened days or weeks earlier — based on signals 
                you never saw.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Callout Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-purple-200 p-8 text-center"
        >
          <p className="text-lg text-slate-700">
            <span className="font-display font-bold text-purple-600">Protego</span> monitors the same 50+ signals Amazon uses — 
            and shows you the rate of change, not just the snapshot. 
            <span className="font-medium"> See what Amazon sees, before they act.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default LaggingIndicatorDeep;
